/**
 * OnboardingAnchorHighlight — навешивает `data-onboarding-focused="true"`
 * на DOM-элемент текущего якоря, чтобы каждый anchor-компонент мог в
 * своём CSS подсветить себя сам (overrides disabled-states, ярче glow,
 * собственный pulse).
 *
 * JB 2026-05-26: «надо чтобы кнопка подсвечивалась и была видна как
 * когда указывает на апгрейды». Тусклая disabled-кнопка кристаллизации
 * терялась даже под focus ring'ом — теперь сама кнопка становится
 * активной/яркой пока tip с её якорем висит.
 *
 * Polls каждые 250ms — если компонент перерендеривается с новым DOM
 * элементом (например AtlasToggle после close AtlasScreen), атрибут
 * вешается на новый узел.
 */
import { useEffect } from 'react';
import { getAnchorElement } from './anchors.js';
import { useOnboardingStore } from './store.js';

const FOCUS_ATTR = 'data-onboarding-focused';

export function OnboardingAnchorHighlight(): null {
  const anchor = useOnboardingStore((s) => s.current?.anchor ?? null);

  useEffect(() => {
    if (anchor === null) return;
    let lastEl: HTMLElement | null = null;
    const sync = (): void => {
      const el = getAnchorElement(anchor);
      // 2026-05-26 BUG FIX: проверка isConnected на lastEl. Если старый
      // anchor element удалён из DOM (например, conditional render
      // компонента) — removeAttribute упадёт молча или повесится на
      // detached node. Принудительно clear ref и продолжаем.
      if (lastEl?.isConnected === false) {
        lastEl = null;
      }
      if (el === lastEl) return;
      // Снимаем со старого только если он ещё в DOM, ставим на новый.
      if (lastEl?.isConnected === true) lastEl.removeAttribute(FOCUS_ATTR);
      if (el !== null) el.setAttribute(FOCUS_ATTR, 'true');
      lastEl = el;
    };
    sync();
    const id = window.setInterval(sync, 250);
    return () => {
      window.clearInterval(id);
      if (lastEl?.isConnected === true) lastEl.removeAttribute(FOCUS_ATTR);
    };
  }, [anchor]);

  return null;
}
